import LiquidGlassCard from "@/components/landing/LiquidGlassCard"
import { Button } from "@/components/ui/button"
import { BreathingGradient } from "@/components/ui/living-brand"
import { SFIcon } from "@/components/ui/sf-icon"
import { Heading, Text } from "@/components/ui/typography"
import { sfIcons } from '@/lib/sf-icons'
import { cn } from "@/lib/utils"
import { semanticTokens } from "@supabank/config/theme"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

type HowItWorksSectionProps = {
  id?: string
}

export function HowItWorksSection({ id }: HowItWorksSectionProps) {
  const { t } = useTranslation("marketing")

  // Steps from SSOT (marketing.json)
  const steps = ((t("howItWorks.steps", { returnObjects: true }) as any[]) || []).map((step: any) => ({
    id: step.id,
    title: step.title,
    description: step.description,
    icon: step.icon ? (sfIcons[step.icon as keyof typeof sfIcons] || sfIcons.info) : sfIcons.info,
    learnMoreHref: step.learnMoreHref || "#",
  }))

  return (
    <section
      id={id}
      className={cn(
        "relative py-16 sm:py-24 overflow-hidden",
        "outline-none", // Remove any focus outline (section is not interactive)
        semanticTokens.bg.surface
      )}
      aria-labelledby="landing-how-it-works"
      tabIndex={-1} // Prevent focus outline (section is not interactive)
    >
      {/* Color Lab: Mint Fresh gradient (Cash App style) */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse at 30% 70%, hsla(142, 71%, 45%, 0.12) 0%, transparent 50%),
            radial-gradient(ellipse at 70% 30%, hsla(160, 84%, 39%, 0.08) 0%, transparent 50%)
          `
        }}
      />
      {/* Living Brand: Breathing Gradient */}
      <BreathingGradient
        variant="mint"
        syncWithLed
        className="absolute inset-0 z-[1] pointer-events-none opacity-15"
      />
      <div className="relative z-10 mx-auto max-w-4xl space-y-8 sm:space-y-10 md:space-y-12 px-4 sm:px-6">
        <div className="space-y-3 sm:space-y-4 text-center">
          <Text variant="caption" color="muted" className="uppercase tracking-widest font-medium">
            {t("howItWorks.badge")}
          </Text>
          <Heading level={2} id="landing-how-it-works">
            {t("howItWorks.headline")}{" "}
            {t("howItWorks.headlineHighlight") && (
              <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                {t("howItWorks.headlineHighlight")}
              </span>
            )}
          </Heading>
          {t("howItWorks.description") && (
            <Text variant="lg" color="muted" className="mx-auto max-w-2xl">
              {t("howItWorks.description")}
            </Text>
          )}
        </div>

        {/* Steps Grid - Icons inside cards for clean alignment */}
        <ol className="space-y-4 sm:space-y-6">
          {steps.map((step, index) => {
            const Icon = step.icon
            return (
              <li
                key={step.id}
                className={cn(
                  'min-w-0', // Prevent flex/grid overflow
                  'motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2',
                  semanticTokens.motion.reduced
                )}
                style={{ animationDelay: `${index * 120}ms` }}
              >
                <LiquidGlassCard
                  tier="surface"
                  className="min-w-0"
                >
                  <>
                    {index < steps.length - 1 && (
                      <span
                        aria-hidden="true"
                        className={cn(
                          'absolute left-5 top-10 h-full w-px rounded-full',
                          semanticTokens.harmony.calm.border.replace('border', 'bg'),
                          'motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-2'
                        )}
                      />
                    )}
                    {/* Icon Badge */}
                    <div className="absolute left-0 top-0">
                      <LiquidGlassCard tier="surface" className="p-2">
                        <SFIcon
                          icon={Icon}
                          size="md"
                          variant="primary"
                          className={cn(semanticTokens.accent.fg)}
                          aria-hidden="true"
                        />
                      </LiquidGlassCard>
                    </div>
                    {/* Step Content - LiquidGlassCard handles all visual styling */}
                    <LiquidGlassCard
                      tier="surface"
                      className="min-w-0"
                    >
                    <div className="p-4 sm:p-6">
                      <div className="space-y-3 sm:space-y-4">
                        <Heading level={3}>
                          {step.title}
                        </Heading>
                        {step.description && (
                          <Text variant="md" color="muted">
                            {step.description}
                          </Text>
                        )}
                        {step.learnMoreHref && step.learnMoreHref !== "#" && (
                          <Link
                            to={step.learnMoreHref}
                            className={cn(
                              "inline-flex items-center gap-2 text-sm font-medium",
                              semanticTokens.accent.fg,
                              "hover:underline",
                              semanticTokens.focus.ring,
                              "min-h-[44px] touch-manipulation"
                            )}
                          >
                            {t("howItWorks.learnMore")}
                            <sfIcons.externalLink className="h-4 w-4" aria-hidden="true" />
                          </Link>
                        )}
                      </div>
                    </div>
                  </LiquidGlassCard>
                  </>
                </LiquidGlassCard>
              </li>
            )
          })}
        </ol>

        {/* CTA */}
        <div className="text-center mt-8 sm:mt-10">
          <Button
            asChild
            variant="default"
            className={cn(
              "gap-2",
              "min-h-[44px] touch-manipulation",
              semanticTokens.focus.ring
            )}
          >
            <Link to={t("howItWorks.cta.href")}>
              {t("howItWorks.cta.label")}
              <sfIcons.next className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  )
}
